import {
  ActionIcon,
  Alert,
  Box,
  Button,
  Checkbox,
  Flex,
  Group,
  Paper,
  Radio,
  SimpleGrid,
  Stack,
  Switch,
  Text,
  TextInput,
} from "@mantine/core";
import { IconPlus, IconTrash } from "@tabler/icons-react";
import { Dispatch, SetStateAction, useMemo, useState } from "react";
import { BrandStripEditor } from "../BrandStripEditor";
import { CategoryTreeSelect, useCategoryImage } from "../CategoryTreeSelect";
import {
  DragHandle,
  reorderList,
  SectionCard,
  SortableArea,
  useSortableItem,
  ZoomableImage,
} from "../HomeBlockShared";
import {
  BrandRef,
  ProductBadge,
  ProductConfig,
  ProductQuickLink,
  ProductTab,
} from "../homeBlockConfig";
import { ACCENT, HomeBlock } from "../homeBlockData";
import classes from "../homeBlock.module.css";
import { parseCodes, ProductCodePicker } from "../ProductCodePicker";
import { Crop, ImagePicker, newId, ThumbPicker, useBlockConfig } from "./EditorKit";

const BADGE_OPTIONS: { value: ProductBadge; label: string }[] = [
  { value: "discount", label: "% giảm giá" },
  { value: "best_seller", label: "Bán chạy #1" },
  { value: "gift", label: "Quà tặng kèm" },
  { value: "sold_bar", label: "Thanh đã bán" },
];

const QUICK_COLUMNS = "20px 56px minmax(0, 1fr) minmax(0, 1fr) 30px";
const MAX_QUICK = 6;

// Banner mẫu cắt từ ảnh thiết kế của từng block: chỉ để xem khi chưa có ảnh thật, không lưu
const SAMPLE_BANNER_CROP: Record<string, Crop> = {
  "home-laptop": { x: 20, y: 40, w: 642, h: 222 },
  "home-pc": { x: 665, y: 40, w: 593, h: 220 },
  "home-monitor": { x: 0, y: 0, w: 1280, h: 245 },
  "home-office": { x: 0, y: 0, w: 790, h: 235 },
};

// Tên sản phẩm mẫu, dùng tạm khi chưa tra được từ API
const SAMPLE_PRODUCT_NAMES: Record<string, string> = {
  LTAU1135: "Laptop Asus Gaming ROG Strix G614PR-TS114W",
  VGAA5006: "Laptop MSI Gaming Katana 15 B13VGK",
  TNHY032: "MacBook Air M3 13 inch 2024",
};

const newTab = (name: string, productCodes = ""): ProductTab => ({
  id: newId(),
  name,
  productCodes,
  categoryId: null,
  badges: ["discount", "best_seller", "gift", "sold_bar"],
});

const usesCategory = (tab: ProductTab) => tab.categoryId != null;

// Danh mục mẫu ghi theo đường dẫn để tự khớp với cây danh mục thật
const quickLinks = (items: [string, string][]): ProductQuickLink[] =>
  items.map(([name, path]) => ({ id: newId(), name, category: { id: null, name: path }, image: null }));

const brands = (names: string[]): BrandRef[] => names.map((name) => ({ id: null, name }));

type SampleParts = Pick<ProductConfig, "heading" | "notice" | "quickLinks" | "tabs"> & {
  bannerLink: string;
  bannerAlt: string;
  brandNames: string[];
};

const buildConfig = (parts: SampleParts): ProductConfig => ({
  heading: parts.heading,
  banner: { desktopImage: null, mobileImage: null, link: parts.bannerLink, alt: parts.bannerAlt },
  brandStrip: {
    enabled: parts.brandNames.length > 0,
    brands: brands(parts.brandNames),
    allLabel: parts.brandNames.length > 0 ? "Xem tất cả" : "",
    allLink: parts.brandNames.length > 0 ? "/thuong-hieu" : "",
  },
  notice: parts.notice,
  quickLinks: parts.quickLinks,
  tabs: parts.tabs,
  defaultTabId: parts.tabs[0].id,
});

const NOTICE_OFF: ProductConfig["notice"] = {
  enabled: false,
  title: "",
  description: "",
  buttonLabel: "",
  link: "",
};

const laptopSample = () =>
  buildConfig({
    heading: {
      title: "LAPTOP - MACBOOK - TABLET",
      allLabel: "Xem tất cả Laptop",
      allLink: "/laptop-may-tinh-xach-tay",
    },
    bannerLink: "/laptop-ai-asus",
    bannerAlt: "Laptop AI ASUS thông minh hơn cho mọi nhu cầu",
    brandNames: ["ASUS", "MSI", "Lenovo", "Dell", "Acer", "Apple", "Microsoft"],
    notice: {
      enabled: true,
      title: "Copilot+ PC thế hệ mới đã có mặt tại HACOM",
      description: "Hiệu suất cao hơn. Thông minh hơn. Sáng tạo hơn.",
      buttonLabel: "Khám phá ngay",
      link: "/copilot-pc",
    },
    quickLinks: quickLinks([
      ["Laptop Văn phòng", "Laptop > Laptop Văn phòng"],
      ["Laptop Gaming", "Laptop > Laptop Gaming"],
      ["Laptop AI", "Laptop > Laptop AI"],
      ["Laptop Đồ họa", "Laptop > Laptop Đồ họa"],
      ["Tablet", "Tablet"],
      ["Phụ kiện Laptop", "Phụ kiện > Phụ kiện Laptop"],
    ]),
    tabs: [
      newTab("Bán chạy", "LTAU1135,VGAA5006,TNHY032"),
      newTab("Laptop Gaming", "LTAU1135,VGAA5006"),
      newTab("Laptop Văn phòng"),
      newTab("Macbook", "TNHY032"),
      newTab("Laptop AI"),
      newTab("Laptop Đồ họa"),
      newTab("Tablet"),
      newTab("Phụ kiện Laptop"),
    ],
  });

const pcSample = () =>
  buildConfig({
    heading: {
      title: "PC - MINI PC - ALL IN ONE",
      allLabel: "Xem tất cả PC",
      allLink: "/pc-may-tinh-de-ban",
    },
    bannerLink: "/pc-hieu-nang-cao",
    bannerAlt: "PC hiệu năng cao cho mọi nhu cầu",
    brandNames: ["ASUS", "MSI", "Gigabyte", "Intel", "AMD"],
    notice: {
      enabled: true,
      title: "Build PC theo nhu cầu",
      description:
        "Tư vấn cấu hình miễn phí, tối ưu hiệu năng theo ngân sách, kiểm tra tương thích linh kiện, hỗ trợ lắp ráp tận nơi.",
      buttonLabel: "Build PC ngay",
      link: "/build-pc",
    },
    quickLinks: quickLinks([
      ["PC Gaming", "PC > PC Gaming"],
      ["PC Đồ họa", "PC > PC Đồ họa"],
      ["PC All in One", "PC > PC All in One"],
      ["Mini PC", "PC > Mini PC"],
      ["PC Văn phòng", "PC > PC Văn phòng"],
    ]),
    tabs: [
      newTab("Sản phẩm nổi bật"),
      newTab("PC Gaming"),
      newTab("PC Đồ họa"),
      newTab("PC Văn phòng"),
      newTab("PC All in One"),
      newTab("Mini PC"),
      newTab("Linh kiện PC"),
    ],
  });

const monitorSample = () =>
  buildConfig({
    heading: { title: "MÀN HÌNH", allLabel: "Xem tất cả Màn hình", allLink: "/man-hinh-may-tinh" },
    bannerLink: "/man-hinh",
    bannerAlt: "Màn hình hiển thị sắc nét, trải nghiệm vượt trội",
    // Thiết kế block Màn hình (block 8) không có dải thương hiệu
    brandNames: [],
    notice: NOTICE_OFF,
    quickLinks: quickLinks([
      ["Màn Hình Gaming", "Màn hình > Màn hình Gaming"],
      ["Màn Hình Đồ Họa", "Màn hình > Màn hình Đồ họa"],
      ["Màn Hình Văn Phòng", "Màn hình > Màn hình Văn phòng"],
      ["Màn Hình OLED", "Màn hình > Màn hình OLED"],
      ["TV OLED 4K", "TV > TV OLED 4K"],
      ["Giá Treo & Arm Màn Hình", "Phụ kiện > Giá treo màn hình"],
    ]),
    tabs: [
      newTab("Bán chạy"),
      newTab("24 inch"),
      newTab("27 inch"),
      newTab("32 inch"),
      newTab("34 inch"),
      newTab("2K QHD"),
      newTab("4K UHD"),
      newTab("240Hz+"),
      newTab("Mới ra mắt"),
    ],
  });

const officeSample = () =>
  buildConfig({
    heading: {
      title: "VĂN PHÒNG - DOANH NGHIỆP",
      allLabel: "Chính sách tốt cho doanh nghiệp",
      allLink: "/chinh-sach-doanh-nghiep",
    },
    bannerLink: "/van-phong",
    bannerAlt: "Giải pháp thiết bị văn phòng đồng bộ, hiện đại",
    // Thiết kế block Văn phòng - Doanh nghiệp không có dải thương hiệu
    brandNames: [],
    notice: NOTICE_OFF,
    quickLinks: quickLinks([
      ["Máy in", "Văn phòng > Máy in"],
      ["Máy chấm công", "Văn phòng > Máy chấm công"],
      ["Photo", "Văn phòng > Máy photocopy"],
      ["Máy hủy tài liệu", "Văn phòng > Máy hủy tài liệu"],
      ["Máy chiếu", "Văn phòng > Máy chiếu"],
      ["Thiết bị hội nghị", "Văn phòng > Thiết bị hội nghị"],
    ]),
    tabs: [
      newTab("Bán chạy"),
      newTab("In văn phòng"),
      newTab("In laser"),
      newTab("Máy chiếu phòng họp"),
      newTab("Hội nghị trực tuyến"),
      newTab("Chấm công vân tay"),
      newTab("POS bán hàng"),
      newTab("Mới ra mắt"),
    ],
  });

const emptyConfig = (block: HomeBlock) =>
  buildConfig({
    heading: { title: block.name.toUpperCase(), allLabel: "", allLink: "" },
    bannerLink: "",
    bannerAlt: "",
    brandNames: [],
    notice: NOTICE_OFF,
    quickLinks: [],
    tabs: [newTab("Bán chạy")],
  });

const initialConfig = (block: HomeBlock): ProductConfig => {
  if (block.code === "home-laptop") return laptopSample();
  if (block.code === "home-pc") return pcSample();
  if (block.code === "home-monitor") return monitorSample();
  if (block.code === "home-office") return officeSample();
  return emptyConfig(block);
};

const QuickRow = ({
  tile,
  onChange,
  onRemove,
}: {
  tile: ProductQuickLink;
  onChange: (patch: Partial<ProductQuickLink>) => void;
  onRemove: () => void;
}) => {
  const { setNodeRef, style, handleProps } = useSortableItem(tile.id);
  const categoryImage = useCategoryImage(tile.category.id);
  return (
    <div
      ref={setNodeRef}
      className={classes.listRow}
      style={{ ...style, gridTemplateColumns: QUICK_COLUMNS, background: "#fff" }}
    >
      <DragHandle label={`Kéo để đổi thứ tự ${tile.name}`} handleProps={handleProps} size={16} />
      <ThumbPicker
        label={`ô ${tile.name}`}
        src={tile.image}
        fallbackSrc={categoryImage}
        fallbackNote="Ảnh của danh mục"
        onPick={(url) => onChange({ image: url })}
        onReset={() => onChange({ image: null })}
        resetLabel="Dùng lại ảnh của danh mục"
      />
      <TextInput
        aria-label="Tên ô"
        value={tile.name}
        onChange={(e) => onChange({ name: e.currentTarget.value })}
      />
      <CategoryTreeSelect
        ariaLabel={`Danh mục liên kết của ${tile.name}`}
        value={tile.category.id}
        fallbackName={tile.category.name}
        onChange={(id, node) =>
          onChange({ category: node ? { id: node.id, name: node.name } : { id, name: "" } })
        }
      />
      <ActionIcon variant="subtle" color="gray" aria-label={`Xoá ô ${tile.name}`} onClick={onRemove}>
        <IconTrash size={16} />
      </ActionIcon>
    </div>
  );
};

const TabItem = ({
  tab,
  active,
  onPick,
}: {
  tab: ProductTab;
  active: boolean;
  onPick: () => void;
}) => {
  const { setNodeRef, style, handleProps } = useSortableItem(tab.id);
  const count = parseCodes(tab.productCodes).length;
  const status = usesCategory(tab) ? "Danh mục" : count > 0 ? `${count} SP` : "Trống";
  return (
    <div ref={setNodeRef} style={style}>
      <Group gap={0} wrap="nowrap">
        <DragHandle label={`Kéo để đổi thứ tự tab ${tab.name}`} handleProps={handleProps} size={14} />
        <button
          type="button"
          role="tab"
          aria-selected={active}
          className={`${classes.ptab} ${active ? classes.ptabActive : ""}`}
          onClick={onPick}
        >
          <Text component="span" fz={14} fw="inherit" truncate style={{ flex: "1 1 auto" }}>
            {tab.name || "Tab chưa đặt tên"}
          </Text>
          <Text
            component="span"
            fz={12}
            fw={500}
            className={classes.muted}
            style={{ whiteSpace: "nowrap" }}
          >
            {status}
          </Text>
        </button>
      </Group>
    </div>
  );
};

const EditProduct = ({ block }: { block: HomeBlock }) => {
  const [config, setConfig] = useBlockConfig(block, () => initialConfig(block));
  const [activeTabId, setActiveTabId] = useState(config.tabs[0].id);
  const sampleBannerCrop = SAMPLE_BANNER_CROP[block.code];
  // Banner mẫu cắt từ thiết kế: chỉ để xem, gỡ thì ẩn; không nằm trong config
  const [showSampleBanner, setShowSampleBanner] = useState(!!sampleBannerCrop);
  // Tab đang để chế độ "Chọn từ danh mục" nhưng chưa chọn danh mục (config chỉ lưu id danh mục)
  const [categoryModeById, setCategoryModeById] = useState<Record<string, boolean>>({});

  const { heading, banner, brandStrip, notice, tabs } = config;
  const patch = <K extends keyof ProductConfig>(key: K, value: Partial<ProductConfig[K]>) =>
    setConfig((c) => ({ ...c, [key]: { ...(c[key] as object), ...value } }));

  const setBrands: Dispatch<SetStateAction<BrandRef[]>> = (action) =>
    setConfig((c) => ({
      ...c,
      brandStrip: {
        ...c.brandStrip,
        brands: typeof action === "function" ? action(c.brandStrip.brands) : action,
      },
    }));

  const currentTab = tabs.find((t) => t.id === activeTabId) ?? tabs[0];
  const categoryMode = categoryModeById[currentTab.id] ?? usesCategory(currentTab);
  const currentCodeCount = parseCodes(currentTab.productCodes).length;
  const patchTab = (value: Partial<ProductTab>) =>
    setConfig((c) => ({
      ...c,
      tabs: c.tabs.map((t) => (t.id === currentTab.id ? { ...t, ...value } : t)),
    }));

  const setQuickLinks = (update: (list: ProductQuickLink[]) => ProductQuickLink[]) =>
    setConfig((c) => ({ ...c, quickLinks: update(c.quickLinks) }));

  const hasDesktopBanner = !!banner.desktopImage || (showSampleBanner && !!sampleBannerCrop);

  const checklist = useMemo(() => {
    const items: string[] = [];
    if (!hasDesktopBanner) items.push("Banner lớn: chưa có ảnh máy tính.");
    else if (!banner.mobileImage) items.push("Banner lớn: thiếu ảnh điện thoại.");
    else items.push("Banner lớn: đủ ảnh máy tính và điện thoại.");
    const defaultTab = tabs.find((t) => t.id === config.defaultTabId);
    items.push(`${tabs.length} tab sản phẩm, tab ${defaultTab?.name || "đầu tiên"} mở sẵn.`);
    const emptyTabs = tabs.filter((t) => !usesCategory(t) && parseCodes(t.productCodes).length === 0);
    if (emptyTabs.length > 0) {
      items.push(`${emptyTabs.length} tab chưa chọn sản phẩm hoặc danh mục, sẽ ẩn trên web.`);
    }
    const unmatchedBrands = brandStrip.enabled
      ? brandStrip.brands.filter((b) => b.id === null).length
      : 0;
    if (unmatchedBrands > 0) items.push(`${unmatchedBrands} thương hiệu chưa khớp Danh sách thương hiệu.`);
    const links = [
      heading.allLink,
      banner.link,
      brandStrip.enabled && brandStrip.allLabel ? brandStrip.allLink : "",
      notice.enabled ? notice.link : "",
    ].filter(Boolean);
    items.push(
      links.every((l) => l.startsWith("/"))
        ? "Mọi link đều là đường dẫn trong hacom.vn."
        : "Có link trỏ ra ngoài hacom.vn, kiểm tra lại."
    );
    return items;
  }, [config.defaultTabId, hasDesktopBanner, banner, brandStrip, heading, notice, tabs]);

  const addTab = () => {
    const tab = newTab("Tab mới");
    setConfig((c) => ({ ...c, tabs: [...c.tabs, tab] }));
    setActiveTabId(tab.id);
  };

  const removeTab = () => {
    if (tabs.length <= 1) return;
    const index = tabs.findIndex((t) => t.id === currentTab.id);
    const rest = tabs.filter((t) => t.id !== currentTab.id);
    setConfig((c) => ({
      ...c,
      tabs: c.tabs.filter((t) => t.id !== currentTab.id),
      defaultTabId: c.defaultTabId === currentTab.id ? rest[0].id : c.defaultTabId,
    }));
    setActiveTabId(rest[Math.min(index, rest.length - 1)].id);
  };

  return (
    <Flex wrap="wrap" align="flex-start" gap={16}>
      <Stack gap={16} style={{ flex: "999 1 620px", minWidth: 0 }}>
        <SectionCard title="Thông tin chung">
          <TextInput
            label="Tiêu đề block"
            value={heading.title}
            onChange={(e) => patch("heading", { title: e.currentTarget.value })}
          />
          <SimpleGrid cols={{ base: 1, sm: 2 }} spacing={12}>
            <TextInput
              label="Nhãn liên kết Xem tất cả"
              value={heading.allLabel}
              onChange={(e) => patch("heading", { allLabel: e.currentTarget.value })}
            />
            <TextInput
              label="Link"
              value={heading.allLink}
              onChange={(e) => patch("heading", { allLink: e.currentTarget.value })}
            />
          </SimpleGrid>
        </SectionCard>

        <SectionCard title="Banner lớn">
          <Flex wrap="wrap" gap={12}>
            <Box style={{ flex: "2 1 320px", minWidth: 0 }}>
              <ImagePicker
                label="Ảnh máy tính"
                src={banner.desktopImage}
                imageId={showSampleBanner ? block.thumbId : undefined}
                crop={sampleBannerCrop}
                onPick={(url) => patch("banner", { desktopImage: url })}
                onClear={() => {
                  setShowSampleBanner(false);
                  patch("banner", { desktopImage: null });
                }}
                maxWidth={9999}
              />
            </Box>
            <Box style={{ flex: "1 1 180px", minWidth: 0 }}>
              <ImagePicker
                label="Ảnh điện thoại"
                src={banner.mobileImage}
                onPick={(url) => patch("banner", { mobileImage: url })}
                onClear={() => patch("banner", { mobileImage: null })}
                maxWidth={9999}
              />
            </Box>
          </Flex>
          {hasDesktopBanner && !banner.mobileImage && (
            <Alert color="orange" variant="light" radius="sm" p={10}>
              <Text fz={13}>Chưa có ảnh điện thoại. Trên điện thoại, web sẽ thu nhỏ ảnh máy tính.</Text>
            </Alert>
          )}
          <SimpleGrid cols={{ base: 1, sm: 2 }} spacing={12}>
            <TextInput
              label="Link khi bấm"
              value={banner.link}
              onChange={(e) => patch("banner", { link: e.currentTarget.value })}
            />
            <TextInput
              label="Mô tả ảnh"
              value={banner.alt}
              onChange={(e) => patch("banner", { alt: e.currentTarget.value })}
              description="Dùng cho người đọc màn hình và SEO."
              inputWrapperOrder={["label", "input", "description"]}
            />
          </SimpleGrid>
        </SectionCard>

        <SectionCard
          title="Thương hiệu"
          right={
            <Switch
              color={ACCENT}
              label="Hiện dải thương hiệu"
              checked={brandStrip.enabled}
              onChange={(e) => patch("brandStrip", { enabled: e.currentTarget.checked })}
            />
          }
        >
          {brandStrip.enabled ? (
            <>
              <Text fz={13} className={classes.muted}>
                Chọn từ Danh sách thương hiệu, lưu theo id. Logo lấy theo thương hiệu.
              </Text>
              <BrandStripEditor value={brandStrip.brands} onChange={setBrands} />
              <SimpleGrid cols={{ base: 1, sm: 2 }} spacing={12}>
                <TextInput
                  label="Nhãn nút cuối dải logo"
                  placeholder="Ví dụ: Xem tất cả"
                  description="Để trống thì ẩn nút."
                  inputWrapperOrder={["label", "input", "description"]}
                  value={brandStrip.allLabel}
                  onChange={(e) => patch("brandStrip", { allLabel: e.currentTarget.value })}
                />
                <TextInput
                  label="Link nút"
                  placeholder="/thuong-hieu"
                  value={brandStrip.allLink}
                  disabled={!brandStrip.allLabel}
                  onChange={(e) => patch("brandStrip", { allLink: e.currentTarget.value })}
                />
              </SimpleGrid>
            </>
          ) : (
            <Text fz={13} className={classes.muted}>
              Block này không có dải thương hiệu. Bật lên nếu cần hiện logo thương hiệu.
            </Text>
          )}
        </SectionCard>

        <SectionCard
          title="Dải thông báo"
          right={
            <Switch
              color={ACCENT}
              label="Hiện dải thông báo"
              checked={notice.enabled}
              onChange={(e) => patch("notice", { enabled: e.currentTarget.checked })}
            />
          }
        >
          {notice.enabled ? (
            <SimpleGrid cols={{ base: 1, sm: 2 }} spacing={12}>
              <TextInput
                label="Tiêu đề"
                value={notice.title}
                onChange={(e) => patch("notice", { title: e.currentTarget.value })}
              />
              <TextInput
                label="Mô tả"
                value={notice.description}
                onChange={(e) => patch("notice", { description: e.currentTarget.value })}
              />
              <TextInput
                label="Nhãn nút"
                value={notice.buttonLabel}
                onChange={(e) => patch("notice", { buttonLabel: e.currentTarget.value })}
              />
              <TextInput
                label="Link"
                value={notice.link}
                onChange={(e) => patch("notice", { link: e.currentTarget.value })}
              />
            </SimpleGrid>
          ) : (
            <Text fz={13} className={classes.muted}>
              Dải thông báo đang tắt.
            </Text>
          )}
        </SectionCard>

        <SectionCard
          title="Ô danh mục nhanh"
          bodyGap={0}
          right={
            <Text fz={13} className={classes.muted}>
              {config.quickLinks.length} / {MAX_QUICK} ô · ảnh lấy từ danh mục, bấm ảnh để đổi
            </Text>
          }
        >
          <div
            className={`${classes.listRow} ${classes.listRowHeader}`}
            style={{ gridTemplateColumns: QUICK_COLUMNS }}
          >
            <span />
            <span>Ảnh</span>
            <span>Tên ô</span>
            <span>Danh mục liên kết</span>
            <span />
          </div>
          <SortableArea
            ids={config.quickLinks.map((q) => q.id)}
            onReorder={(from, to) => setQuickLinks((list) => reorderList(list, from, to))}
          >
            {config.quickLinks.map((tile) => (
              <QuickRow
                key={tile.id}
                tile={tile}
                onChange={(value) =>
                  setQuickLinks((list) => list.map((q) => (q.id === tile.id ? { ...q, ...value } : q)))
                }
                onRemove={() => setQuickLinks((list) => list.filter((q) => q.id !== tile.id))}
              />
            ))}
          </SortableArea>
          {config.quickLinks.length < MAX_QUICK ? (
            <Group mt={10}>
              <Button
                size="xs"
                variant="default"
                leftSection={<IconPlus size={14} />}
                onClick={() =>
                  setQuickLinks((list) => [
                    ...list,
                    { id: newId(), name: "", category: { id: null, name: "" }, image: null },
                  ])
                }
              >
                Thêm ô
              </Button>
            </Group>
          ) : (
            <Text fz={12} mt={10} className={classes.muted}>
              Đủ 6 ô. Xoá bớt một ô để thêm ô mới.
            </Text>
          )}
        </SectionCard>

        <SectionCard
          title="Tab sản phẩm"
          bodyPadding={0}
          bodyGap={0}
          right={
            <Button size="xs" variant="default" leftSection={<IconPlus size={14} />} onClick={addTab}>
              Thêm tab
            </Button>
          }
        >
          <Flex wrap="wrap" align="stretch">
            <Stack
              gap={2}
              p={10}
              role="tablist"
              aria-orientation="vertical"
              aria-label="Các tab sản phẩm"
              style={{ flex: "1 1 250px", borderRight: "1px solid var(--hb-border)" }}
            >
              <SortableArea
                ids={tabs.map((t) => t.id)}
                onReorder={(from, to) =>
                  setConfig((c) => ({ ...c, tabs: reorderList(c.tabs, from, to) }))
                }
              >
                {tabs.map((tab) => (
                  <TabItem
                    key={tab.id}
                    tab={tab}
                    active={tab.id === currentTab.id}
                    onPick={() => setActiveTabId(tab.id)}
                  />
                ))}
              </SortableArea>
            </Stack>
            <Stack gap={14} p={16} role="tabpanel" style={{ flex: "999 1 360px", minWidth: 0 }}>
              <SimpleGrid cols={{ base: 1, sm: 2 }} spacing={12}>
                <TextInput
                  label="Tên tab"
                  value={currentTab.name}
                  onChange={(e) => patchTab({ name: e.currentTarget.value })}
                />
                <Box>
                  <Text fz={14} fw={500} mb={4}>
                    Tab mở sẵn
                  </Text>
                  <Checkbox
                    color={ACCENT}
                    mt={8}
                    label="Mở tab này khi tải trang"
                    checked={config.defaultTabId === currentTab.id}
                    onChange={(e) =>
                      e.currentTarget.checked &&
                      setConfig((c) => ({ ...c, defaultTabId: currentTab.id }))
                    }
                  />
                </Box>
              </SimpleGrid>
              <Radio.Group
                label="Lấy sản phẩm"
                value={categoryMode ? "category" : "products"}
                onChange={(value) => {
                  setCategoryModeById((m) => ({ ...m, [currentTab.id]: value === "category" }));
                  // Về chọn từng sản phẩm thì bỏ danh mục; mã sản phẩm chỉ bỏ khi đã chọn danh mục
                  if (value === "products") patchTab({ categoryId: null });
                }}
              >
                <Group mt={6} gap={20}>
                  <Radio color={ACCENT} value="products" label="Chọn từng sản phẩm" />
                  <Radio color={ACCENT} value="category" label="Chọn từ danh mục" />
                </Group>
              </Radio.Group>
              {categoryMode ? (
                <Box>
                  <CategoryTreeSelect
                    key={currentTab.id}
                    label="Danh mục sản phẩm"
                    value={currentTab.categoryId ?? null}
                    onChange={(id) =>
                      patchTab(id === null ? { categoryId: null } : { categoryId: id, productCodes: "" })
                    }
                  />
                  <Text fz={12} mt={6} className={classes.muted}>
                    {currentCodeCount > 0
                      ? `Chọn danh mục sẽ bỏ ${currentCodeCount} mã sản phẩm đang có của tab. `
                      : ""}
                    Chỉ lưu id danh mục, trang chủ tự lấy sản phẩm thuộc danh mục này.
                  </Text>
                </Box>
              ) : (
                <ProductCodePicker
                  key={currentTab.id}
                  value={currentTab.productCodes}
                  onChange={(value) => patchTab({ productCodes: value })}
                  fallbackNames={SAMPLE_PRODUCT_NAMES}
                />
              )}
              <Checkbox.Group
                label="Nhãn trên thẻ sản phẩm"
                value={currentTab.badges}
                onChange={(value) => patchTab({ badges: value as ProductBadge[] })}
              >
                <Group mt={6} gap={20}>
                  {BADGE_OPTIONS.map((o) => (
                    <Checkbox key={o.value} color={ACCENT} value={o.value} label={o.label} />
                  ))}
                </Group>
              </Checkbox.Group>
              <Group>
                <Button
                  size="xs"
                  variant="subtle"
                  color="red"
                  leftSection={<IconTrash size={14} />}
                  disabled={tabs.length <= 1}
                  onClick={removeTab}
                >
                  Xoá tab này
                </Button>
              </Group>
            </Stack>
          </Flex>
        </SectionCard>
      </Stack>

      <Stack
        component="aside"
        aria-label="Ảnh thiết kế"
        gap={16}
        style={{ flex: "1 1 380px", maxWidth: "100%" }}
      >
        <Paper withBorder radius="md">
          <Text
            component="h2"
            fw={700}
            fz={15}
            m={0}
            px={16}
            py={14}
            style={{ borderBottom: "1px solid var(--hb-border)" }}
          >
            Ảnh thiết kế
          </Text>
          <Box p={16} bg="var(--hb-surface-2)">
            <ZoomableImage imageId={block.thumbId} alt={`Ảnh thiết kế ${block.name}`} />
          </Box>
          <Text fz={12} px={16} pt={10} pb={14} className={classes.muted}>
            Ảnh thiết kế gốc để đối chiếu, không đổi theo nội dung đang sửa.
          </Text>
        </Paper>
        <Paper withBorder radius="md">
          <Text
            component="h2"
            fw={700}
            fz={15}
            m={0}
            px={16}
            py={14}
            style={{ borderBottom: "1px solid var(--hb-border)" }}
          >
            Kiểm tra trước khi lưu
          </Text>
          <Stack component="ul" gap={8} m={0} py={12} pr={16} pl={34}>
            {checklist.map((item) => (
              <Text component="li" key={item} fz={13} lh={1.45} c="var(--hb-ink-2)">
                {item}
              </Text>
            ))}
          </Stack>
        </Paper>
      </Stack>
    </Flex>
  );
};

export default EditProduct;
